import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';

const DocumentForm = ({
    doc,
    formData,
    setFormData,
    onClose,
    commonFields,
    documentFields,
    onSubmit,
    isEdit
}) => {
    const [customFields, setCustomFields] = useState([]);
    const [errors, setErrors] = useState({});
    const [newFieldName, setNewFieldName] = useState('');
    const [newFieldValue, setNewFieldValue] = useState('');
    const [newFieldType, setNewFieldType] = useState('text'); 

    const inputTypes = ['text', 'number', 'email', 'date', 'tel']; 

    useEffect(() => {
        const existingAdditionalData = formData.additional_data || {};
        const specificFields = (documentFields[formData.document_type] || []).map(field => ({
            name: field.toLowerCase().replace(/ /g, '_'),
            value: existingAdditionalData[field.toLowerCase().replace(/ /g, '_')] || formData[field.toLowerCase().replace(/ /g, '_')] || '',
            inputType: 'text' 
        }));
        const customFieldsFromData = Object.entries(existingAdditionalData)
            .filter(([key]) => !specificFields.some(f => f.name === key))
            .map(([name, value]) => ({ name, value, inputType: 'text' }));

        setCustomFields([...specificFields, ...customFieldsFromData]);
    }, [formData, documentFields]);

    const validateField = (name, value, inputType) => {
        value = value.trim();

        if (inputType === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(value) ? '' : 'Please enter a valid email address';
        }
        if (inputType === 'tel') {
            const phoneRegex = /^\d{10}$/;
            return phoneRegex.test(value) ? '' : 'Please enter a valid 10-digit phone number';
        }
        if (name.includes('aadhar')) {
            const aadharRegex = /^\d{12}$/;
            return aadharRegex.test(value) ? '' : 'Please enter a valid 12-digit Aadhar number';
        }
        if (name.includes('pan')) {
            const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
            return panRegex.test(value) ? '' : 'Please enter a valid PAN number (e.g., ABCDE1234F)';
        }
        if (name.includes('voter')) {
            const voterRegex = /^[A-Z]{3}\d{7}$/;
            return voterRegex.test(value) ? '' : 'Please enter a valid Voter ID (e.g., ABC1234567)';
        }
        if (inputType === 'number' && value && !/^\d*$/.test(value)) {
            return 'Please enter a valid number';
        }
        return '';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value, getInputType(name));
        setErrors(prev => ({ ...prev, [name]: error }));
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCustomFieldChange = (index, key, value) => {
        const updatedFields = [...customFields];
        updatedFields[index] = { ...updatedFields[index], [key]: value };

        if (key === 'value') {
            const fieldName = updatedFields[index].name;
            const inputType = updatedFields[index].inputType || 'text';
            const error = validateField(fieldName, value, inputType);
            setErrors(prev => ({ ...prev, [fieldName]: error }));
        }

        setCustomFields(updatedFields);

        const additionalData = updatedFields.reduce((acc, field) => {
            if (field.name && field.value && field.name.trim() !== '' && field.value.trim() !== '') {
                acc[field.name.trim()] = field.value.trim();
            }
            return acc;
        }, {});
        setFormData(prev => ({ ...prev, additional_data: additionalData }));
    };

    const addCustomField = () => {
        if (newFieldName.trim() && newFieldValue.trim()) {
            setCustomFields(prev => [...prev, { name: newFieldName.trim(), value: newFieldValue.trim(), inputType: newFieldType }]);
            setNewFieldName('');
            setNewFieldValue('');
            setNewFieldType('text');

            const additionalData = [...customFields, { name: newFieldName.trim(), value: newFieldValue.trim(), inputType: newFieldType }].reduce((acc, field) => {
                if (field.name && field.value) {
                    acc[field.name] = field.value;
                }
                return acc;
            }, {});
            setFormData(prev => ({ ...prev, additional_data: additionalData }));
        }
    };

    const removeCustomField = (index) => {
        const updatedFields = customFields.filter((_, i) => i !== index);
        setCustomFields(updatedFields);

        const additionalData = updatedFields.reduce((acc, field) => {
            if (field.name && field.value && field.name.trim() !== '' && field.value.trim() !== '') {
                acc[field.name.trim()] = field.value.trim();
            }
            return acc;
        }, {});
        setFormData(prev => ({ ...prev, additional_data: additionalData }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};

        commonFields.forEach(field => {
            const error = validateField(field.name, formData[field.name] || '', getInputType(field.name));
            if (error) newErrors[field.name] = error;
        });

        customFields.forEach(field => {
            const error = validateField(field.name, field.value || '', field.inputType || 'text');
            if (error) newErrors[field.name] = error;
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const finalFormData = {
            ...formData,
            document_type: formData.document_type === "N/A" ? null : formData.document_type,
            additional_data: customFields.reduce((acc, field) => {
                if (field.name && field.value && field.name.trim() !== '' && field.value.trim() !== '') {
                    acc[field.name.trim()] = field.value.trim();
                }
                return acc;
            }, {})
        };
        console.log("Submitting form with data:", finalFormData);
        onSubmit(finalFormData);
    };

    const documentType = formData.document_type || doc?.document_type || null;

    const getInputType = (fieldName) => {
        if (fieldName.includes('email')) return 'email';
        if (fieldName.includes('phone') || fieldName.includes('tel')) return 'tel';
        if (fieldName.includes('date')) return 'date';
        if (fieldName.includes('aadhar') || fieldName.includes('pan') || fieldName.includes('voter')) return 'text';
        return 'text';
    };

    return (
        <>
            <div className='offcanvas-header'>
                <h5>{isEdit ? 'Edit Details' : 'Add Details'} </h5>
                <button onClick={onClose} className="bg-transparent" ><i className='bi bi-x-circle'></i></button>
            </div>
            <div className="offcanvas-body" >
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label">Document Type</label>
                        <input
                            type="text"
                            value={documentType === "N/A" ? "null" : documentType || "null"}
                            className="form-control"
                            readOnly
                            disabled
                        />
                    </div>

                    {commonFields
                        .filter(field => field.name !== 'document_type')
                        .map(field => (
                            <div key={field.name} className="mb-3">
                                <label className="form-label">{field.label}</label>
                                <input
                                    type={getInputType(field.name)}
                                    name={field.name}
                                    value={formData[field.name] || ''}
                                    onChange={handleInputChange}
                                    className={`form-control ${errors[field.name] ? 'is-invalid' : ''}`}
                                />
                                {errors[field.name] && (
                                    <div className="invalid-feedback">
                                        {errors[field.name]}
                                    </div>
                                )}
                            </div>
                        ))}

                    <div className="mb-3">
                        {customFields.map((field, index) => (
                            <div key={index} className="my-2 ">
                                <label className="form-label">
                                    {field.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </label>
                                <input
                                    type={field.inputType || 'text'}
                                    value={field.value}
                                    onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                                    className={`form-control mt-2 ${errors[field.name] ? 'is-invalid' : ''}`}
                                />
                                {/* <button 
                                    type="button" 
                                    onClick={() => removeCustomField(index)} 
                                    className="btn btn-danger"
                                >
                                    <FaTrash />
                                </button> */}
                                {errors[field.name] && (
                                    <div className="invalid-feedback">
                                        {errors[field.name]}
                                    </div>
                                )}
                            </div>
                        ))}

                        <div className="mt-3">
                            <div className=" gap-2 align-items-center d-flex">
                                <div className="w-75">
                                    <input
                                        type="text"
                                        value={newFieldName}
                                        onChange={(e) => setNewFieldName(e.target.value)}
                                        placeholder="Field Name"
                                        className="form-control mt-2"
                                    />
                                    <select
                                        value={newFieldType}
                                        onChange={(e) => setNewFieldType(e.target.value)}
                                        className="form-select mt-2"
                                    >
                                        {inputTypes.map(type => (
                                            <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        value={newFieldValue}
                                        onChange={(e) => setNewFieldValue(e.target.value)}
                                        placeholder="Value"
                                        className="form-control mt-2"
                                    />
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        onClick={addCustomField}
                                        className="btn btn-success"
                                        disabled={!newFieldName.trim() || !newFieldValue.trim()}
                                        style={{ width: '150px' }}
                                    >
                                        <FaPlus /> Add
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="modal-footer border-2 border-top py-2">
                        <button type="submit" className="btn btn-primary">Save</button>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default DocumentForm;